import {
  TrendingDown,
  TrendingUp,
  Check,
  CircleDollarSign,
} from 'lucide-react';
import { memo, useMemo } from 'react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

import type { ISuppliarWithDebt } from '../../api/HandRequest.interface';

interface DebtBadgeProps {
  supplier: ISuppliarWithDebt | null;
}

export const DebtBadge = memo(({ supplier }: DebtBadgeProps) => {
  // Memoize calculated values - placed before early return to avoid conditional hook
  const { formattedAmount, statusInfo } = useMemo(() => {
    if (supplier?.Debt === undefined) {
      // Default values when supplier debt is undefined
      return {
        formattedAmount: '0',
        statusInfo: {
          icon: Check,
          color: 'warning',
          bgLight: 'bg-gradient-to-br from-amber-50 to-amber-100/80',
          bgDark:
            'dark:bg-gradient-to-br dark:from-amber-900/30 dark:to-amber-800/20',
          borderLight: 'border-amber-200/60',
          borderDark: 'dark:border-amber-700/40',
          textLight: 'text-amber-700',
          textDark: 'dark:text-amber-400',
          pulseLight: 'group-hover:bg-amber-100/30',
          pulseDark: 'dark:group-hover:bg-amber-800/20',
          iconLight: 'text-amber-500',
          iconDark: 'dark:text-amber-400',
          shadowLight: 'shadow-amber-200/30',
          shadowDark: 'dark:shadow-amber-900/40',
        },
      };
    }

    const amount = Math.abs(supplier?.Debt || 0);
    const formatted = new Intl.NumberFormat('en-US').format(amount);

    // Determine status details based on DebtStatus
    const status = {
      icon: Check,
      color: 'warning',
      bgLight: 'bg-gradient-to-br from-amber-50 to-amber-100/80',
      bgDark:
        'dark:bg-gradient-to-br dark:from-amber-900/30 dark:to-amber-800/20',
      borderLight: 'border-amber-200/60',
      borderDark: 'dark:border-amber-700/40',
      textLight: 'text-amber-700',
      textDark: 'dark:text-amber-400',
      pulseLight: 'group-hover:bg-amber-100/30',
      pulseDark: 'dark:group-hover:bg-amber-800/20',
      iconLight: 'text-amber-500',
      iconDark: 'dark:text-amber-400',
      shadowLight: 'shadow-amber-200/30',
      shadowDark: 'dark:shadow-amber-900/40',
    };

    if (supplier?.DebtStatus === 'مديون') {
      status.icon = TrendingUp;
      status.color = 'destructive';
      status.bgLight = 'bg-gradient-to-br from-rose-50 to-red-100/80';
      status.bgDark =
        'dark:bg-gradient-to-br dark:from-rose-950/40 dark:to-red-900/30';
      status.borderLight = 'border-rose-200/60';
      status.borderDark = 'dark:border-rose-800/40';
      status.textLight = 'text-rose-700';
      status.textDark = 'dark:text-rose-400';
      status.pulseLight = 'group-hover:bg-rose-100/30';
      status.pulseDark = 'dark:group-hover:bg-rose-900/20';
      status.iconLight = 'text-rose-500';
      status.iconDark = 'dark:text-rose-400';
      status.shadowLight = 'shadow-rose-200/30';
      status.shadowDark = 'dark:shadow-rose-900/40';
    } else if (supplier?.DebtStatus === 'دائن') {
      status.icon = TrendingDown;
      status.color = 'success';
      status.bgLight = 'bg-gradient-to-br from-emerald-50 to-green-100/80';
      status.bgDark =
        'dark:bg-gradient-to-br dark:from-emerald-950/40 dark:to-green-900/30';
      status.borderLight = 'border-emerald-200/60';
      status.borderDark = 'dark:border-emerald-800/40';
      status.textLight = 'text-emerald-700';
      status.textDark = 'dark:text-emerald-400';
      status.pulseLight = 'group-hover:bg-emerald-100/30';
      status.pulseDark = 'dark:group-hover:bg-emerald-900/20';
      status.iconLight = 'text-emerald-500';
      status.iconDark = 'dark:text-emerald-400';
      status.shadowLight = 'shadow-emerald-200/30';
      status.shadowDark = 'dark:shadow-emerald-900/40';
    }

    return { formattedAmount: formatted, statusInfo: status };
  }, [supplier?.Debt, supplier?.DebtStatus]);

  // Return null early if no data is available
  if (supplier?.Debt === undefined) return null;

  // Destructure status info for easier use
  const {
    icon: StatusIcon,
    color,
    bgLight,
    bgDark,
    borderLight,
    borderDark,
    textLight,
    textDark,
    pulseLight,
    pulseDark,
    iconLight,
    iconDark,
    shadowLight,
    shadowDark,
  } = statusInfo;

  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-lg border',
        'bg-white/90 dark:bg-gray-800/90',
        'backdrop-blur-sm transition-all duration-300',
        'hover:scale-[1.01] hover:border-border/70 hover:shadow-lg',
        borderLight,
        borderDark,
        'shadow-sm',
        shadowLight,
        shadowDark,
      )}
    >
      {/* Background pulse effect on hover */}
      <div
        className={cn(
          'absolute inset-0 -z-10 opacity-0 transition-opacity duration-300',
          pulseLight,
          pulseDark,
          'group-hover:opacity-100',
        )}
      />

      <div className="flex items-center p-2">
        {/* Debt currency icon with animated background */}
        <div className="relative mr-3 flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-md">
          <div className={cn('absolute inset-0 -z-10', bgLight, bgDark)} />
          <div
            className={cn(
              'absolute inset-0 -z-20 animate-pulse-slow opacity-40 blur-md',
              bgLight,
              bgDark,
            )}
          />
          <CircleDollarSign
            className={cn('h-5 w-5', iconLight, iconDark)}
            strokeWidth={1.5}
          />
        </div>

        {/* Debt title and amount */}
        <div className="flex flex-col px-1">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
            المديونية
          </p>
          <p className="font-mono text-sm font-semibold tracking-tight text-gray-900 dark:text-gray-50">
            {formattedAmount}
          </p>
        </div>

        {/* Status badge with icon */}
        <Badge
          variant="outline"
          className={cn(
            'ml-2 flex items-center gap-1.5 rounded-md border px-2 py-0.5',
            'border-opacity-30 dark:border-opacity-40',
            textLight,
            textDark,
            borderLight,
            borderDark,
            'bg-white/70 dark:bg-gray-900/70',
            'transition-all duration-300 group-hover:bg-white/90 dark:group-hover:bg-gray-900/90',
          )}
          color={color as any}
        >
          <StatusIcon className="h-3.5 w-3.5" strokeWidth={2.5} />
          <span className="text-xs font-medium">{supplier?.DebtStatus}</span>
        </Badge>
      </div>
    </div>
  );
});

DebtBadge.displayName = 'DebtBadge';
